import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  EditIcon,
  EyeIcon,
  EyeOffIcon,
  TrashIcon,
} from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import type { TemplateListItemDTO } from "@/modules/template/domain/contracts";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";

type TemplateRow = TemplateListItemDTO;

type TemplateTableProps = {
  data: TemplateRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (template: TemplateRow) => void;
  onEdit: (template: TemplateRow) => void;
  onDelete?: (template: TemplateRow) => void;
  onActivate?: (template: TemplateRow) => void;
  onDeactivate?: (template: TemplateRow) => void;
};

export function TemplateTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
}: TemplateTableProps) {
  const { has } = usePermissions();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            ໃຊ້ງານ
          </Badge>
        );
      case "deprecated":
        return <Badge variant="secondary">ປັບປຸງ</Badge>;
      case "archived":
        return <Badge variant="destructive">ບໍ່ໃຊ້ງານ</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const columns: TanstackReactTable.ColumnDef<TemplateRow>[] = [
    createSortableColumn<TemplateRow>("name", "ຊື່ຮ່າງເອກະສານ", { size: 200 }),
    {
      id: "categoryName",
      header: "ປະເພດຮ່າງເອກະສານ",
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.categoryName || row.original.categoryCode || "-"}
        </span>
      ),
      size: 150,
    },
    {
      id: "departments",
      header: "ພະແນກທີ່ໃຊ້",
      cell: ({ row }) => (
        <div className="flex max-w-xs flex-wrap gap-1">
          {row.original.departments?.slice(0, 3).map((dept) => (
            <Badge key={dept.departmentId} variant="outline" className="text-xs">
              {dept.departmentName}
            </Badge>
          ))}
          {row.original.departments &&
            row.original.departments.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{row.original.departments.length - 3}
              </Badge>
            )}
          {(!row.original.departments || row.original.departments.length === 0) && (
            <span className="text-sm text-muted-foreground">-</span>
          )}
        </div>
      ),
      size: 200,
    },
    {
      id: "currentVersion",
      header: "ເວີຊັນລ່າສຸດ",
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.currentVersion || "-"}
        </span>
      ),
      size: 120,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => getStatusBadge(row.original.status),
      size: 100,
    },
    createSortableColumn<TemplateRow>("updatedAt", "ວັນທີອັບເດດລ່າສຸດ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-sm">
          {formatDateTimeLocal(row.original.updatedAt)}
        </span>
      ),
    }),
    {
      size: 120,
      id: "actions",
      cell: ({ row }: { row: { original: TemplateRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          onClick: () => void;
          variant?: "destructive";
        }[] = [];

        if (has(Permissions.template.read)) {
          actions.push({
            label: "ເບິ່ງລາຍລະອຽດ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original),
          });
        }

        if (has(Permissions.template.update)) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });

          // Activate/Deactivate actions based on status
          const isActive = row.original.status === "active";
          if (isActive && onDeactivate) {
            actions.push({
              label: "ປິດການໃຊ້ງານ",
              icon: <EyeOffIcon className="h-4 w-4" />,
              onClick: async () => {
                const ok = await confirm({
                  title: "ປິດການໃຊ້ງານຮ່າງເອກະສານ",
                  description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະປິດການໃຊ້ງານຮ່າງເອກະສານ "${row.original.name}"?`,
                  actionText: "ປິດການໃຊ້ງານ",
                  cancelText: "ຍົກເລີກ",
                });
                if (ok) {
                  onDeactivate(row.original);
                }
              },
            });
          } else if (!isActive && onActivate) {
            actions.push({
              label: "ເປີດການໃຊ້ງານ",
              icon: <EyeIcon className="h-4 w-4" />,
              onClick: async () => {
                const ok = await confirm({
                  title: "ເປີດການໃຊ້ງານຮ່າງເອກະສານ",
                  description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະເປີດການໃຊ້ງານຮ່າງເອກະສານ "${row.original.name}"?`,
                  actionText: "ເປີດການໃຊ້ງານ",
                  cancelText: "ຍົກເລີກ",
                });
                if (ok) {
                  onActivate(row.original);
                }
              },
            });
          }
        }

        if (has(Permissions.template.delete) && onDelete) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບຮ່າງເອກະສານ",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຮ່າງເອກະສານ "${row.original.name}"? ການກະທໍານີ້ບໍ່ສາມາດຍົກເລີກໄດ້.`,
                actionText: "ລຶບ",
                cancelText: "ຍົກເລີກ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                onDelete(row.original);
              }
            },
          });
        }

        return <RowActions actions={actions} />;
      },
    },
  ];

  return (
    <div data-tourid="template-table">
      <DataTable<TemplateRow, unknown>
        noDataMessage="ບໍ່ພົບຮ່າງເອກະສານ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
    </div>
  );
}
